import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { courseReportPage } from "../constants/reportPages"
import {
  exportCoursesAsCSV,
  GetCourseParams,
  getPaginationMeta,
  useGetCoursesQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"
import {
  formatBoolean,
  formatDate,
  formatDateTime,
  formatNullable,
  toIsoDateTime,
} from "./reportFormatters"

type CourseDateFilters = Pick<
  GetCourseParams,
  "created_start" | "created_end" | "updated_start" | "updated_end"
>

const columns: GridColDef[] = [
  {
    field: "title",
    headerName: "Title",
    width: 260,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.title),
  },
  {
    field: "objective",
    headerName: "Objective",
    width: 300,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.objective),
  },
  {
    field: "published",
    headerName: "Published",
    width: 120,
    sortable: false,
    renderCell: (params) => (
      <Chip
        label={params.row.published ? "Yes" : "No"}
        color={params.row.published ? "success" : "default"}
        size="small"
      />
    ),
  },
  {
    field: "draft",
    headerName: "Draft",
    width: 100,
    sortable: false,
    valueGetter: (_value, row) => formatBoolean(row.draft),
  },
  {
    field: "archived",
    headerName: "Archived",
    width: 110,
    sortable: false,
    valueGetter: (_value, row) => formatBoolean(row.archived),
  },
  {
    field: "mobile_friendly",
    headerName: "Mobile",
    width: 100,
    sortable: false,
    valueGetter: (_value, row) => formatBoolean(row.mobile_friendly),
  },
  {
    field: "estimated_time",
    headerName: "Est. Time",
    width: 110,
    type: "number",
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.estimated_time),
  },
  {
    field: "level",
    headerName: "Level",
    width: 140,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.level),
  },
  {
    field: "submission_type",
    headerName: "Submission",
    width: 150,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.submission_type),
  },
  {
    field: "release_date",
    headerName: "Release",
    width: 130,
    sortable: false,
    valueGetter: (_value, row) => formatDate(row.release_date),
  },
  {
    field: "created",
    headerName: "Created",
    width: 170,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.created),
  },
  {
    field: "updated",
    headerName: "Updated",
    width: 170,
    sortable: false,
    valueGetter: (_value, row) => formatDateTime(row.updated),
  },
]

function CourseReportPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    created_start: "",
    created_end: "",
    updated_start: "",
    updated_end: "",
  })
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<CourseDateFilters>
  >({})

  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } = useGetCoursesQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const handleFilterChange = (
    field: keyof CourseDateFilters,
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    const createdStartMs = filters.created_start
      ? Date.parse(filters.created_start)
      : null
    const createdEndMs = filters.created_end
      ? Date.parse(filters.created_end)
      : null
    const updatedStartMs = filters.updated_start
      ? Date.parse(filters.updated_start)
      : null
    const updatedEndMs = filters.updated_end
      ? Date.parse(filters.updated_end)
      : null

    if (
      [createdStartMs, createdEndMs, updatedStartMs, updatedEndMs].some(
        (value) => value !== null && Number.isNaN(value)
      )
    ) {
      setValidationError("One or more dates are invalid.")
      return
    }

    if (
      createdStartMs !== null &&
      createdEndMs !== null &&
      createdStartMs > createdEndMs
    ) {
      setValidationError("Created start date must be before created end date.")
      return
    }

    if (
      updatedStartMs !== null &&
      updatedEndMs !== null &&
      updatedStartMs > updatedEndMs
    ) {
      setValidationError("Updated start date must be before updated end date.")
      return
    }

    const nextFilters: Partial<CourseDateFilters> = {}

    if (createdStartMs !== null) {
      nextFilters.created_start = toIsoDateTime(filters.created_start)
    }
    if (createdEndMs !== null) {
      nextFilters.created_end = toIsoDateTime(filters.created_end)
    }
    if (updatedStartMs !== null) {
      nextFilters.updated_start = toIsoDateTime(filters.updated_start)
    }
    if (updatedEndMs !== null) {
      nextFilters.updated_end = toIsoDateTime(filters.updated_end)
    }

    setValidationError(null)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(nextFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setValidationError(null)
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      created_start: "",
      created_end: "",
      updated_start: "",
      updated_end: "",
    })
    setAppliedFilters({})
  }

  const handleExportCSV = () => {
    exportCoursesAsCSV({
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    })
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={courseReportPage.pageTitle}
        description={courseReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Created Date Range"
              startDate={filters.created_start}
              endDate={filters.created_end}
              onStartDateChange={(value) =>
                handleFilterChange("created_start", value)
              }
              onEndDateChange={(value) =>
                handleFilterChange("created_end", value)
              }
            />
            <DateRangeFilter
              label="Updated Date Range"
              startDate={filters.updated_start}
              endDate={filters.updated_end}
              onStartDateChange={(value) =>
                handleFilterChange("updated_start", value)
              }
              onEndDateChange={(value) =>
                handleFilterChange("updated_end", value)
              }
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {validationError && <Alert severity="warning">{validationError}</Alert>}

      {error && (
        <Alert severity="error">Error loading courses. Please try again.</Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) => row.id ?? row.uuid}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default CourseReportPage
